import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import dropdownIcon from 'assets/images/icons/dropdown-arrow-white.svg';
import NestedSelect from 'components/form-components/NestedSelect';
import AlertModal from 'components/common-components/AlertModal';
import TicketEditModal from './TicketEditModal';
import { CallAPI } from 'actions/General';
import { permissionKeys } from 'config/config';
import { isPermissionAvailable } from 'config/permissionUtils';
import { ticketPriorityFilterOptions, ticketStatusUpdateOptions } from '../utils';
import { cloneDeep } from 'lodash';

const TicketActions = ({ ids, selectedRows, actionsCallBack }) => {
  const { t } = useTranslation();

  const [showModal, setShowModal] = useState(null);
  const [ticketActionsOptions, setTicketActionsOptions] = useState([]);

  const getUpdatedStatusOptions = (selectedRows) => {
    if (selectedRows.length === 0) return [];
    const modifiedStatusOptions = cloneDeep(ticketStatusUpdateOptions);
    modifiedStatusOptions.forEach((o) => {
      if (o.value === 'in-progress')
        o.disabled = !selectedRows.every((row) => row.status === 'open');
      else if (o.value === 'resolved')
        o.disabled = !selectedRows.every(
          (row) => row.status === 'open' || row.status === 'in-progress',
        );
    });
    return modifiedStatusOptions;
  };

  useEffect(() => {
    const allOptions = [
      {
        value: 'edit',
        displayValue: 'select_edit',
        permission:
          isPermissionAvailable(permissionKeys.field_work_tickets_create) && ids.length === 1,
      },
      {
        value: 'status',
        displayValue: 'select_status',
        permission: isPermissionAvailable(permissionKeys.field_work_tickets_create),
        nestedOptions: getUpdatedStatusOptions(selectedRows),
      },
      {
        value: 'priority',
        displayValue: 'select_priority',
        permission: isPermissionAvailable(permissionKeys.field_work_tickets_create),
        nestedOptions: ticketPriorityFilterOptions.filter((option) => !!option.value),
      },
      {
        value: 'delete',
        displayValue: 'select_delete',
        permission: isPermissionAvailable(permissionKeys.field_work_tickets_delete),
      },
    ];

    const visibleOptions = allOptions.filter((option) => option.permission);

    setTicketActionsOptions(visibleOptions);
  }, [ids.length]);

  const handleCloseModal = () => {
    setShowModal(null);
  };

  const handleAction = (name, value) => {
    if (name === 'ticketAction') setShowModal(value);
    else if (name === 'status' || name === 'priority') handleBulkDataChange(name, value);
  };

  const handleBulkDataChange = async (field, value) => {
    const payload = { ids, updatedDetails: { [field]: value } };
    let result = await CallAPI('UPDATE_TICKETS', payload);
    if (result.status) {
      toast.success(t('alert_success_ticket_updated'));
      actionsCallBack();
    }
  };

  const handleDeleteTicket = async () => {
    const result = await CallAPI('DELETE_TICKETS', { ids });
    if (result.status) {
      toast.success(t('alert_success_ticket_deleted'));
      actionsCallBack();
    }
  };

  return (
    <>
      {ticketActionsOptions.length && (
        <NestedSelect
          options={ticketActionsOptions}
          name='ticketAction'
          disabled={ids.length === 0}
          handleChange={handleAction}
          toggleClasses='d-flex gap-2 px-3 py-2 theme-background-green-1 user-select-none'
          toggleComponent={
            <>
              <label className='cursor-pointer theme-text-white-1'>{t('button_action')}</label>
              <img src={dropdownIcon} />
            </>
          }
        />
      )}

      {showModal === 'edit' && (
        <TicketEditModal
          selectedRecordId={ids[0]}
          handleClose={handleCloseModal}
          actionsCallBack={actionsCallBack}
        />
      )}

      {showModal === 'delete' && (
        <AlertModal
          show
          showCloseButton
          closeModal={handleCloseModal}
          variant='delete'
          message={'alert_delete_ticket'}
          actionButtons={[
            {
              text: t('button_confirm'),
              variant: 'red-1',
              onClick: () => {
                handleCloseModal();
                handleDeleteTicket();
              },
            },
            {
              text: t('button_cancel'),
              variant: 'white-1',
              onClick: handleCloseModal,
            },
          ]}
        />
      )}
    </>
  );
};

TicketActions.propTypes = {
  ids: PropTypes.array.isRequired,
  selectedRows: PropTypes.array.isRequired,
  actionsCallBack: PropTypes.func.isRequired,
};

export default TicketActions;
